import { useCallback, useEffect } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { ArrowBigUp, Plus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { canCreateFlows } from "../utils/can-create-flows"

type StartNewFlowButtonProps = {
  className?: string
  iconOnly?: boolean
}

export function StartNewFlowButton({ className, iconOnly = false }: StartNewFlowButtonProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().includes("MAC")

  const handleClick = useCallback(() => {
    void navigate("/flows")
  }, [navigate])

  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "o") {
        event.preventDefault()
        handleClick()
      }
    }

    window.addEventListener("keydown", handleGlobalKeyDown)
    return () => window.removeEventListener("keydown", handleGlobalKeyDown)
  }, [handleClick])

  // Fas 1a: hide the entry point when flow creation is restricted to admins.
  if (!canCreateFlows()) {
    return null
  }

  const shortcutHint = (
    <kbd className="bg-muted text-muted-foreground dark:border-border dark:bg-muted/50 inline-flex h-5 items-center justify-center gap-1 rounded border px-1.5 text-[10px] font-medium select-none">
      <span className="text-xs leading-tight">{isMac ? "⌘" : "Ctrl"}</span>
      <ArrowBigUp className="size-3" />
      <span className="font-sans text-[11px] leading-tight">O</span>
    </kbd>
  )

  if (iconOnly) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid="start-new-flow-button-icon"
            size="icon"
            variant="ghost"
            className={cn(
              "size-10 rounded-md transition-[color,background-color,box-shadow,scale] duration-200",
              "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1",
              "active:scale-[0.99]",
              className
            )}
            onClick={handleClick}
          >
            <Plus className="size-6" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p className="flex items-center gap-2">
            {t("workflowLanding.newWorkflow")}
            {shortcutHint}
          </p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Button
      data-testid="start-new-flow-button-large"
      variant="ghost"
      className={cn(
        "group/new text-foreground h-9 w-full justify-start rounded-lg px-2 text-sm transition-[color,background-color,box-shadow,scale] duration-150",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1",
        "active:scale-[0.98]",
        className
      )}
      onClick={handleClick}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Plus className="size-4 shrink-0" />
          <span className="translate-y-px truncate font-semibold">
            {t("workflowLanding.newWorkflow")}
          </span>
        </div>
        <span className="opacity-0 transition-opacity group-hover/new:opacity-100 group-focus-visible/new:opacity-100">
          {shortcutHint}
        </span>
      </div>
    </Button>
  )
}
